import { Link } from 'react-router-dom'
import { ArrowLeft, Image } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToImageGallery } from '@/components/gallery/TextToImageGallery'
import { ImageGalleryItem } from '@/models/gallery'
import { TextImageToImageGallery } from '@/components/gallery/TextImageToImageGallery'
import { styles } from '@/styles/ImagePage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'FLUX Pro',
    shortName: 'Flux Pro',
    modelKey: 'flux-pro',
    creditsPerImage: 6,
    heroBgImage: 'https://cdn.aibrush.co/docs/images/models/flux-pro/image-hero.jpg',
    providerUrl: 'https://bfl.ai/',
    providerName: 'Black Forest Labs',
    providerModelUrl: 'https://bfl.ai/models/flux-pro',
    providerKontextUrl: 'https://bfl.ai/models/flux-kontext',
    pageUrl: 'https://docs.aibrush.co/images-models/flux-pro',
} as const;

const textToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "flux-pro-1",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-1.jpg",

        prompt: "Panther, colossal in size majestic"
    },
    {
        id: "flux-pro-2",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-2.jpg",

        prompt: "Extreme close-up of Raavi's trembling wrists bound tightly in rusted iron chains."
    },
    {
        id: "flux-pro-3",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-3.jpg",
        variant: 'tall',
        prompt: "A minimalist fashion portrait of a woman in a sleek black turtleneck, seated on a modern white chair in an all-white room. Her gaze drifts off-camera, creating a cool, detached elegance. The composition is clean, with strong contrast and subtle shadow work. black and white photo"
    },
    {
        id: "flux-pro-4",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-4.jpg",
        variant: 'wide',
        prompt: "a Chinese boy on boat, horrified, jaw dropped, looking at the camera, Pixar-style dramatic moment"
    },
    {
        id: "flux-pro-5",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-5.jpg",

        prompt: "photo portrait of old man amd his beloved fox winter forest"
    },
    {
        id: "flux-pro-6",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-6.jpg",

        prompt: "A rapper holding a microphone high and rapping in front of an audience, expressed in silhouette, with an animated feel and a dreamy atmosphere."
    },
    {
        id: "flux-pro-7",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-7.jpg",

        prompt: "ultra-realistic wide-angle action shot, an orange tabby cat wearing a sleek racing helmet and black racing suit, driving a formula cart skidding at high speed on Brno racing track, cat leaning to the side with wide startled eyes and slightly panicked open mouth, humorous tense expression as if losing control but still gripping the steering wheel tightly, sparks and tire smoke, futuristic Brno city skyline in background, rival racing cats blurred in motion, cinematic lighting, vibrant colors, strong motion blur, extreme sharpness on cat, immersive motorsport photography aesthetic"
    },
    {
        id: "flux-pro-8",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-8.jpg",

        prompt: "a clear hyper-realistic a fashion show that took place during the Joseon Dynasty in Korea, white glow"
    },
    {
        id: "flux-pro-9",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-9.jpg",
        variant: 'wide',
        prompt: "panning low angle shot of an mysterious runner, motion blur, cinematic lighting, against a dusty pink gradient background, shot on the 35mm lens, high contrast with sharp details and cool tones, high fashion editorial, subtle film grain"
    },
    {
        id: "flux-pro-12",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-12.jpg",
        variant: 'wide',
        prompt: "Cinematic video shot of a Red Bull can partially stuck in the snow, standing upright with a slight tilt. The scene begins from a wide aerial view of a snowy mountain landscape and slowly zooms in toward the can. As the camera approaches, the Red Bull can becomes the focal point showcasing its bold blue and silver design, the twin bulls logo, and condensation droplets glistening under cold natural light. Soft snow surrounds the base, with subtle vapor or mist rising around it. The atmosphere feels intense, clean, and energetic, evoking freshness and power in a frozen environment."
    },
    {
        id: "flux-pro-10",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-10.jpg",

        prompt: "An ultra-realistic high-resolution frame of a brown anthropomorphic cat sitting like a human at a computer desk. The cat is intensely focused on the glowing monitor, rapidly typing on the keyboard with one paw. In the other paw, it holds a lit cigarette between its fingers, puffing out thin smoke streams. Its expression is concentrated, but one eye is squinting slightly, reacting to cigarette smoke drifting toward its face. Warm indoor lighting, soft smoke trails in the air, glowing screen reflection on the cat's face, cinematic and moody atmosphere"
    },
    {
        id: "flux-pro-11",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-11.jpg",

        prompt: "A terrifying T-Rex charging through the jungle, breaking trees in its path, its jaws wide open, chasing a group of humans running in panic, cinematic action scene, ultra-realistic detail"
    }
];

const textImageToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "flux-pro-2",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-14.jpg",

        prompt: "The old man and his fox are grocery shopping",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-5.jpg"]
    },
    {
        id: "flux-pro-1",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-13.jpg",

        prompt: "It's now snowing, everything is covered in snow",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-1.jpg"]
    },
    {
        id: "flux-pro-7",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-15.jpg",

        prompt: "Add sunglasses for the cat",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-7.jpg"]
    },
    {
        id: "flux-pro-10",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-16.jpg",

        prompt: "Transform this image into a Ghibli anime style",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-10.jpg"]
    },
    {
        id: "flux-pro-6",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-17.jpg",

        prompt: "Add text to the image 'Let's Party' and small text below 'By AiBrush'",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-6.jpg"]
    },
    {
        id: "flux-pro-3",
        width: 1080,
        height: 1920,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-18.jpg",
        variant: 'tall',
        prompt: "Make the image colorful. The girl has a red dress and is standing in a bar.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-3.jpg"]
    },
    {
        id: "flux-pro-12",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-19.jpg",
        variant: 'wide',
        prompt: "remove the Red Bull can and add red lipstick",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-12.jpg"]
    },
    {
        id: "flux-pro-11",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-20.jpg",

        prompt: "Add Trump, with dirty clothes, facing the T‑Rex. Trump is in the foreground, close to the camera, running from the T‑Rex.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-11.jpg"]
    },
    {
        id: "flux-pro-8",
        width: 1920,
        height: 1080,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-21.jpg",

        prompt: `make this image a movie poster with a name '잔상의 그림자'
write in english below: 'Shadow of the Afterimage'`,
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-8.jpg"]
    },
];

const allTextImageToImage: string[] = [
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-5.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-1.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-7.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-10.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-6.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-3.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-12.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-11.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-8.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-14.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-13.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-15.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-16.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-17.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-18.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-19.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-20.jpg",
    "https://cdn.aibrush.co/docs/images/models/flux-pro/gallery-21.jpg",
];

export default function FluxProPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroBackground)}>
                    <img
                        src={MODEL_CONFIG.heroBgImage}
                        {...stylex.props(styles.heroBgImage)}
                        alt={`${MODEL_CONFIG.name} hero image`}
                    />
                    <div {...stylex.props(styles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Experience cutting-edge AI image generation with <strong>Text to Image</strong> and <strong>Text & Image to Image</strong> capabilities.
                        Unmatched speed, quality, and prompt adherence.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text & Image to Image</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerImage} credits per image</div>
                </div>
            </section>

            {/* Image Gallery */}
            <TextToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text to Image Gallery"
                description={`Explore the stunning capabilities of <a href='${MODEL_CONFIG.providerModelUrl}' target='_blank' rel='noopener noreferrer'>FLUX1.1 [pro]</a> through these AI-generated images. <strong>Click on any image</strong> to see how it was created.`}
                items={textToImageGalleryItems} />


            {/* Text & Image to Image Gallery */}
            <TextImageToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text & Image to Image Gallery"
                description={`Explore <a href='${MODEL_CONFIG.providerKontextUrl}' target='_blank' rel='noopener noreferrer'>FLUX.1 Kontext</a> State of the art in-context image editing. <strong>Click on any image</strong> to see how it was created.`}
                items={textImageToImageGalleryItems}
                allImages={allTextImageToImage}
            />
        </div>
    )
}
